// ============================================================================
// buildTheme — constructs the MUI theme entirely from brandTokens.
// No color literals here; everything derives from ./brandTokens.
// Mirror of admin-frontend/src/common/theme/buildTheme.ts — keep in sync.
// ============================================================================
import { createTheme, Theme } from '@mui/material/styles';
import {
  ramps,
  semantic,
  rolesFor,
  fonts,
  radius,
  spacingBase,
  shadowFor,
  dialogShadowFor,
  scrimFor,
  hexToRGBA,
} from './brandTokens';

// Register custom Button variants (soft, ghost) on the MUI type system.
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    soft: true;
    ghost: true;
  }
}

export function buildTheme(isDark: boolean): Theme {
  const r = rolesFor(isDark);
  const shadow = shadowFor(isDark);
  const dialogShadow = dialogShadowFor(isDark);
  const scrim = scrimFor(isDark);
  const focusRing = `0 0 0 3px ${hexToRGBA(r.primary, 0.22)}`;

  return createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: { main: r.primary, dark: r.primaryHover, contrastText: r.onPrimary },
      secondary: { main: r.accent, light: r.accentBright, contrastText: r.onAccent },
      error: { main: semantic.error.main, light: semantic.error.tint, dark: semantic.error.dark },
      warning: { main: semantic.warning.main, light: semantic.warning.tint, dark: semantic.warning.dark },
      success: { main: semantic.success.main, light: semantic.success.tint, dark: semantic.success.dark },
      info: { main: semantic.info.main, light: semantic.info.tint, dark: semantic.info.dark },
      background: { default: r.bg, paper: r.paper },
      divider: r.divider,
      text: { primary: r.text, secondary: r.text2, disabled: r.text3 },
    },

    shape: { borderRadius: radius.default },
    spacing: spacingBase,

    typography: {
      fontFamily: fonts.ui,
      h1: { fontFamily: fonts.brand, fontWeight: 300, fontSize: '56px', letterSpacing: '-0.015em', lineHeight: 1.02 },
      h2: { fontFamily: fonts.brand, fontWeight: 300, fontSize: '40px', letterSpacing: '-0.005em', lineHeight: 1.08 },
      h3: { fontFamily: fonts.brand, fontWeight: 400, fontSize: '30px' },
      h4: { fontFamily: fonts.brand, fontWeight: 400, fontSize: '22px', letterSpacing: '0.0075em' },
      h5: { fontFamily: fonts.brand, fontWeight: 400, fontSize: '18px' },
      h6: { fontFamily: fonts.brand, fontWeight: 600, fontSize: '16px' },
      subtitle1: { fontFamily: fonts.ui, fontWeight: 600, fontSize: '16px' },
      subtitle2: { fontFamily: fonts.ui, fontWeight: 600, fontSize: '14px' },
      body1: { fontFamily: fonts.ui, fontWeight: 400, fontSize: '15px' },
      body2: { fontFamily: fonts.ui, fontWeight: 400, fontSize: '13.5px' },
      button: { fontFamily: fonts.brand, fontWeight: 600, fontSize: '14px', letterSpacing: '0.06em', textTransform: 'uppercase' },
      overline: { fontFamily: fonts.brand, fontWeight: 600, fontSize: '12px', letterSpacing: '0.16em', textTransform: 'uppercase' },
      caption: { fontFamily: fonts.ui, fontSize: '12px', letterSpacing: '0.033em' },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: r.bg, color: r.text },
        },
      },

      MuiButton: {
        defaultProps: { size: 'small', disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: radius.default,
            padding: '4px 15px',
            textTransform: 'uppercase',
            fontWeight: 600,
            letterSpacing: '0.06em',
          },
        },
        variants: [
          {
            props: { variant: 'soft' },
            style: {
              backgroundColor: hexToRGBA(r.accentBright, isDark ? 0.16 : 0.12),
              color: isDark ? r.accentBright : ramps.accent[700],
              border: `1px solid ${hexToRGBA(r.accentBright, isDark ? 0.3 : 0.4)}`,
              '&:hover': { backgroundColor: hexToRGBA(r.accentBright, isDark ? 0.24 : 0.2) },
            },
          },
          {
            props: { variant: 'ghost' },
            style: {
              backgroundColor: 'transparent',
              color: r.primary,
              border: `1px solid ${hexToRGBA(r.primary, isDark ? 0.35 : 0.28)}`,
              '&:hover': { backgroundColor: hexToRGBA(r.primary, 0.08), borderColor: r.primary },
            },
          },
        ],
      },

      MuiIconButton: { defaultProps: { size: 'small' } },

      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          rounded: { borderRadius: radius.large },
          elevation1: { boxShadow: shadow },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: { borderRadius: radius.large, boxShadow: shadow, backgroundImage: 'none' },
        },
      },

      MuiTextField: { defaultProps: { size: 'small' } },
      MuiFormControl: { defaultProps: { margin: 'dense' } },
      MuiSelect: { defaultProps: { size: 'small' } },
      MuiInputBase: {
        styleOverrides: { input: { color: 'inherit' } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.default,
            backgroundColor: r.paper,
            '&.Mui-focused': { boxShadow: focusRing },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: r.primary, borderWidth: 1 },
          },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: r.elevated,
            border: `1px solid ${r.divider}`,
            borderRadius: 10,
            boxShadow: shadow,
            padding: 6,
          },
          list: { padding: 0 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: radius.default,
            minHeight: 32,
            fontSize: 13,
            '&.Mui-selected': {
              backgroundColor: hexToRGBA(r.primary, 0.12),
              color: r.primary,
              fontWeight: 600,
              '&:hover': { backgroundColor: hexToRGBA(r.primary, 0.16) },
            },
          },
        },
      },

      MuiChip: {
        defaultProps: { size: 'small' },
        styleOverrides: { root: { borderRadius: radius.pill } },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: r.elevated,
            color: r.text,
            border: `1px solid ${r.divider}`,
            fontSize: 12,
            borderRadius: radius.default,
            boxShadow: shadow,
          },
          arrow: { color: r.elevated },
        },
      },

      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'default' },
        styleOverrides: {
          root: {
            backgroundColor: r.paper,
            color: r.text,
            borderBottom: `1px solid ${r.divider}`,
            backgroundImage: 'none',
          },
        },
      },

      MuiTable: { defaultProps: { size: 'small' } },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: r.divider },
          head: { fontFamily: fonts.brand, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: r.text2 },
        },
      },

      MuiLink: {
        defaultProps: { underline: 'hover' },
        styleOverrides: { root: { color: r.primary } },
      },

      MuiBackdrop: {
        styleOverrides: {
          root: { backgroundColor: scrim },
          // Keep MUI's invisible backdrop (used by menus/popovers) transparent.
          invisible: { backgroundColor: 'transparent' },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: radius.large,
            backgroundImage: 'none',
            border: `1px solid ${r.divider}`,
            boxShadow: dialogShadow,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: fonts.brand,
            fontSize: 20,
            fontWeight: 400,
            letterSpacing: '0.0075em',
            color: r.text,
            padding: '20px 22px 8px',
          },
        },
      },
      MuiDialogContent: { styleOverrides: { root: { padding: '8px 22px' } } },
      MuiDialogContentText: {
        styleOverrides: {
          root: { fontSize: '13.5px', lineHeight: 1.55, color: r.text2 },
        },
      },
      MuiDialogActions: {
        styleOverrides: { root: { padding: '16px 22px 20px' } },
      },
    },
  });
}
